/**
 * Chip Component
 * 
 * A small, compact UI element used to display information or actions.
 * Can be used as a label or as an interactive button.
 * 
 * Features:
 * - Optional icon (left-aligned)
 * - Optional text label
 * - Optional click handler (makes chip interactive). Interactive chips highlight in theme.exclusive
 * - Theme-aware colors
 * - Responsive sizing
 * 
 * Usage:
 * - Static labels: <Chip label="Premium" />
 * - With icon: <Chip iconSrc="/icon.png" label="4K" />
 * - Interactive: <Chip label="Share" onClick={handleShare} />
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

export interface ChipProps {
  /** Text label to display */
  label?: string;

  /** Icon asset path */
  iconPath?: string;

  /** Icon size */
  iconSize?: number;

  /** Icon color - defaults to theme.icon.primary */
  iconColor?: string;

  /** Click handler - makes chip interactive when provided */
  onClick?: () => void;

  /** Additional CSS classes */
  className?: string;

  /** Optional `data-qa-*` set for the chip's clickable `<button>` so the qa-tester can locate it. */
  qa?: QaAttrOptions;
}

export const Chip: React.FC<ChipProps> = ({ label, iconPath, iconSize, iconColor, onClick, className = '', qa }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const isClickable = !!onClick;

  return (
    <button
      {...(qa ? qaAttr(qa) : {})}
      onClick={onClick}
      disabled={!isClickable}
      className={`flex items-center ${isClickable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'} transition-opacity focus:outline-none ${className}`}
      style={{
        // Uncomment if background is desired for interactive chips - currently using only 
        // text/icon color change to indicate interactivity
        // background: isClickable ? theme.secondary : 'transparent',
        // Border radius and padding for `isClickable` only, to maintain compact size when used as static label
        // borderRadius: isClickable ? responsive.borderRadius.sm : 0,
        // padding: isClickable ? `${responsive.spacing.xxs}px ${responsive.spacing.xs}px` : 0,

      }}
    >
      {iconPath && (
        <AssetIcon
          path={iconPath}
          width={iconSize ?? responsive.icon.xxs}
          height={iconSize ?? responsive.icon.xxs}
          color={iconColor ?? isClickable ? theme.icon.primary : theme.icon.secondary}
        />
      )}

      {label && (
        <span
          style={{
            color: isClickable ? theme.text.primary : theme.text.secondary,
            fontSize: responsive.font.sm,
            fontWeight: isClickable ? responsive.fontWeight.semibold : responsive.fontWeight.regular,
            paddingLeft: iconPath ? responsive.spacing.xs : 0,
            // A bit bigger letter spacing for interactive chips to improve readability, 
            // especially with all caps labels like "4K"
            letterSpacing: isClickable ? '0.06em' : 'normal',
          }}
        >
          {label}
        </span>
      )}
    </button>
  );
};
